import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import useSession from '../hooks/useSession';
import AppTheme from '../shared-theme/AppTheme';

const SessionManager = ({ children }) => {
  const navigate = useNavigate();
  const { sessionToken, isValidating, isInitialized, clearSession } = useSession();
  const [showSessionDialog, setShowSessionDialog] = React.useState(false);
  const [sessionMessage, setSessionMessage] = React.useState('');
  const [isCheckingSession, setIsCheckingSession] = React.useState(true);

  // Verificar si hay una sesión activa al cargar
  useEffect(() => {
    const checkInitialSession = async () => {
      // Dar tiempo para que el hook useSession se inicialice completamente
      setTimeout(() => {
        setIsCheckingSession(false);
      }, 2000);
    };

    checkInitialSession();
  }, []);

  // Interceptar errores 401 para manejar sesiones expiradas
  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      if (response.status === 401) {
        const data = await response.json().catch(() => ({}));
        
        if (data.error && (data.error.includes('Sesión inválida') || data.error.includes('Token de sesión'))) {
          setSessionMessage('Tu sesión ha sido invalidada por otro dispositivo. Serás redirigido al login.');
          setShowSessionDialog(true);
        }
      }
      
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const handleSessionClose = () => {
    setShowSessionDialog(false);
    clearSession();
    navigate('/login');
  };

  const handleLoginRedirect = () => {
    clearSession();
    navigate('/login');
  };

  // Solo permitir acceso a páginas públicas sin sesión
  const isPublicPage = ['/login', '/', '/onBoarding','/onboarding'].includes(window.location.pathname);
  const hasUserData = localStorage.getItem('isLoggedIn') === 'true' && localStorage.getItem('id');
  // Debug logs
  console.log('SessionManager Debug:', {
    sessionToken,
    hasUserData,
    isPublicPage,
    isInitialized,
    isCheckingSession,
    pathname: window.location.pathname,
    isLoggedIn: localStorage.getItem('isLoggedIn'),
    userId: localStorage.getItem('id')
  });
  
  // No mostrar el mensaje de sesión requerida hasta que esté inicializado
  //if (!isInitialized || isCheckingSession) {
    //alert(isInitialized+" "+isCheckingSession);
   // return <>{children}</>;
 // }
  
  // Si está en página pública, siempre permitir acceso
  if (isPublicPage) {
    return <>{children}</>;
  }
  
  // Si hay datos de usuario (incluso sin sessionToken), permitir acceso
  if (hasUserData && sessionToken) {
    return <>{children}</>;
  }
  
  // Solo mostrar "Sesión Requerida" si realmente no hay ninguna sesión
  if (!sessionToken && !hasUserData) {
    
    return (
      <AppTheme >
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="info" sx={{ maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Sesión Requerida
          </Typography>
          <Typography variant="body2" gutterBottom>
            Debes iniciar sesión para acceder a esta página.
          </Typography>
          <Button 
            color="primarybutton"
            variant="contained" 
            onClick={handleLoginRedirect}
            sx={{ mt: 2 }}
          >
            Ir al Login
          </Button>
        </Alert>
      </Box>
      </AppTheme>
    );
  }

  return (
    <AppTheme >

      {children}
      
      {/* Dialog para notificaciones de sesión */}
      <Dialog 
        open={showSessionDialog} 
        onClose={handleSessionClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="primary">
            Sesión Expirada
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {sessionMessage}
          </Typography>
          {isValidating && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Verificando sesión...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleSessionClose}
            variant="contained"
            color="primary"
          >
            Ir al Login
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
};

export default SessionManager;
